import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  TextField,
  CircularProgress,
  Alert,
  AlertTitle,
  useTheme,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Avatar,
  Divider,
  Badge
} from '@mui/material';
import { 
  CreditCard as CreditCardIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { 
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import SweetAlert from 'sweetalert2';
import { API_URL, createAxiosInstance } from '../../../api';

const SavedCardsTab = React.memo(({ subscriptionStatus, hasPaid, userId, isActive }) => {
  const theme = useTheme();
  const stripe = useStripe();
  const elements = useElements();
  
  // State
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savedCards, setSavedCards] = useState([]);
  const [addingCard, setAddingCard] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [error, setError] = useState(null);

  // Card element styles
  const cardElementStyles = useMemo(() => ({
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': { color: '#aab7c4' },
      border: '1px solid #e0e0e0',
      borderRadius: '4px',
      padding: '12px',
      marginBottom: '16px',
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
    focus: {
      borderColor: theme.palette.primary.main,
    },
  }), [theme.palette.primary.main]);

  // Fetch saved cards only when tab is active and data hasn't been loaded
  useEffect(() => {
    const fetchSavedCards = async () => {
      if (!userId || !isActive || dataLoaded) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(`${API_URL}/api/billings/cards/${userId}`);
        
        setSavedCards(response.data.cards || []);
        setDataLoaded(true);
        
      } catch (error) {
        console.error('Error fetching saved cards:', error);
        setError('Failed to load saved cards. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSavedCards();
  }, [userId, isActive, dataLoaded]);

  // Handle delete card
  const handleDeleteCard = useCallback(async (cardId) => {
    const result = await SweetAlert.fire({
      title: 'Delete Card',
      html: `
        <div style="display: flex; flex-direction: column; align-items: center; padding: 20px 0;">
          <div style="
            width: 80px;
            height: 80px;
            border-radius: 50%;
            background: linear-gradient(135deg, rgba(113, 45, 148, 0.1), rgba(113, 45, 148, 0.2));
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 20px;
          ">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#712d94" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10 11V17M14 11V17" stroke="#712d94" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <p style="font-size: 16px; color: #333; margin: 0; text-align: center;">
            Are you sure you want to delete this card? This action cannot be undone.
          </p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: theme.palette.primary.main,
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'swal2-popup-custom',
        title: 'swal2-title-custom',
        confirmButton: 'swal2-confirm-custom',
        cancelButton: 'swal2-cancel-custom',
      },
      buttonsStyling: true,
    });

    if (result.isConfirmed) {
      try {
        const axiosInstance = createAxiosInstance();
        await axiosInstance.delete(`${API_URL}/api/billings/cards/${cardId}/${userId}`);
        
        setSavedCards(prevCards => prevCards.filter(card => card.id !== cardId));
        
        SweetAlert.fire({
          title: 'Card Deleted',
          text: 'The card has been removed successfully.',
          icon: 'success',
          confirmButtonText: 'OK'
        });
        
      } catch (error) {
        console.error('Error deleting card:', error);
        SweetAlert.fire({
          title: 'Error',
          text: 'Failed to delete card. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    }
  }, [userId, theme.palette.primary.main]);

  // Handle set as primary card
  const handleSetPrimary = useCallback(async (cardId) => {
    try {
      const axiosInstance = createAxiosInstance();
      await axiosInstance.put(`${API_URL}/api/billings/cards/${cardId}/primary/${userId}`);
      
      // Update the cards to reflect the new primary card
      setSavedCards(prevCards => 
        prevCards.map(card => ({
          ...card,
          isPrimary: card.id === cardId
        }))
      );
      
      SweetAlert.fire({
        title: 'Primary Card Updated',
        text: 'This card is now your primary payment method.',
        icon: 'success',
        confirmButtonText: 'OK'
      });
      
    } catch (error) {
      console.error('Error setting primary card:', error);
      SweetAlert.fire({
        title: 'Error',
        text: 'Failed to set primary card. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  }, [userId]);

  // Handle add new card
  const handleAddCard = useCallback(async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      SweetAlert.fire({
        title: 'Error',
        text: 'Payment system is not ready. Please try again later.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (!cardholderName.trim()) {
      SweetAlert.fire({
        title: 'Error',
        text: 'Please enter the cardholder name.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    setAddingCard(true);

    try {
      const cardElement = elements.getElement(CardNumberElement);
      
      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: { name: cardholderName.trim() }
      });

      if (error) {
        SweetAlert.fire({
          title: 'Error',
          text: error.message,
          icon: 'error',
          confirmButtonText: 'OK'
        });
        setAddingCard(false);
        return;
      }

      // Save payment method to backend
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(`${API_URL}/api/billings/cards/${userId}`, {
        paymentMethodId: paymentMethod.id
      });

      // Only add to state if successful
      if (response.data && response.data.success) {
        setSavedCards(prevCards => [...prevCards, response.data.card]);
        setCardholderName('');
        
        // Clear the card elements
        const cardExpiryElement = elements.getElement(CardExpiryElement);
        const cardCvcElement = elements.getElement(CardCvcElement);
        
        if (cardElement) cardElement.clear();
        if (cardExpiryElement) cardExpiryElement.clear();
        if (cardCvcElement) cardCvcElement.clear();
        
        SweetAlert.fire({
          title: 'Success',
          text: 'Card added successfully!',
          icon: 'success',
          confirmButtonText: 'Great'
        });
      }
      
    } catch (error) {
      console.error('Error adding card:', error);
      
      // Check if it's a duplicate card error
      if (error.response?.data?.isDuplicate) {
        SweetAlert.fire({
          title: 'Card Already Exists',
          html: `
            <div style="padding: 20px 0;">
              <p style="font-size: 16px; color: #333; margin-bottom: 16px;">
                ${error.response.data.message || 'This card is already saved to your account.'}
              </p>
              ${error.response.data.existingCard ? `
                <div style="
                  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                  border: 2px solid #e2e8f0;
                  border-radius: 12px;
                  padding: 16px;
                  margin-top: 12px;
                ">
                  <p style="font-size: 14px; color: #666; margin: 0;">
                    <strong>${error.response.data.existingCard.brand.charAt(0).toUpperCase() + error.response.data.existingCard.brand.slice(1)}</strong> ending in 
                    <strong>${error.response.data.existingCard.last4}</strong>
                  </p>
                </div>
              ` : ''}
            </div>
          `,
          icon: 'info',
          confirmButtonColor: theme.palette.primary.main,
          confirmButtonText: 'Got it'
        });
      } else {
        SweetAlert.fire({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to add card. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } finally {
      setAddingCard(false);
    }
  }, [stripe, elements, cardholderName, userId]);

  // Get card brand icon and color
  const getCardBrandInfo = useCallback((brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return {
          icon: '💳',
          color: '#1A1F71',
          bgColor: '#1A1F71',
          name: 'Visa'
        };
      case 'mastercard':
        return {
          icon: '💳',
          color: '#EB001B',
          bgColor: '#F79E1B',
          name: 'Mastercard'
        };
      case 'amex':
        return {
          icon: '💳',
          color: '#006FCF',
          bgColor: '#006FCF',
          name: 'American Express'
        };
      case 'discover':
        return {
          icon: '💳',
          color: '#FF6000',
          bgColor: '#FF6000',
          name: 'Discover'
        };
      default:
        return {
          icon: '💳',
          color: '#6B7280',
          bgColor: '#6B7280',
          name: 'Card'
        };
    }
  }, []);

  // Format card number for display
  const formatCardNumber = useCallback((last4) => {
    return `•••• •••• •••• ${last4}`;
  }, []);

  // Check if card is expired
  const isCardExpired = useCallback((expMonth, expYear) => {
    if (!expMonth || !expYear) return false;
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    
    if (expYear < currentYear) return true;
    if (expYear === currentYear && expMonth < currentMonth) return true;
    return false;
  }, []);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '300px' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 2.5, sm: 3, md: 4 } }}>
        <Typography variant="h5" sx={{ 
          fontWeight: 700, 
          mb: 1, 
          fontSize: { xs: '1.25rem', sm: '1.35rem', md: '1.5rem' },
          color: theme.palette.primary.main 
        }}>
          Saved Cards
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your saved payment methods for quick and secure transactions.
        </Typography>
      </Box>

      {/* Existing Cards */}
      {savedCards.length > 0 ? (
        <Box sx={{ mb: { xs: 2.5, sm: 3, md: 4 } }}>
          <Typography variant="h6" sx={{ 
            mb: { xs: 2, sm: 2.5, md: 3 }, 
            fontWeight: 600, 
            fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.25rem' },
            color: 'text.primary' 
          }}>
            Your Saved Cards ({savedCards.length})
          </Typography>
          
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            {savedCards.map((card, index) => {
              const brandInfo = getCardBrandInfo(card.card?.brand);
              const isExpired = isCardExpired(card.card?.exp_month, card.card?.exp_year);
              const isPrimary = card.isPrimary || index === 0; // Check if it's the primary card
              
              return (
                <Grid item xs={12} sm={6} md={4} key={card.id}>
                  <Card sx={{
                    position: 'relative',
                    background: `linear-gradient(135deg, ${brandInfo.color}15 0%, ${brandInfo.color}08 100%)`,
                    border: `2px solid ${brandInfo.color}20`,
                    borderRadius: 3,
                    overflow: 'visible'
                    // Removed hover transitions - cards stay static
                  }}>
                    {/* Card Header */}
                    <Box sx={{
                      p: { xs: 2, sm: 2.5, md: 3 },
                      pb: { xs: 1.25, sm: 1.5 },
                      position: 'relative'
                    }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ 
                            fontWeight: 600, 
                            textTransform: 'uppercase',
                            letterSpacing: 1,
                            fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' }
                          }}>
                            {brandInfo.name}
                          </Typography>
                          <Typography variant="h6" sx={{ 
                            fontWeight: 700, 
                            color: brandInfo.color,
                            mt: { xs: 1, sm: 1.25, md: 1.5 },
                            fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                            fontFamily: 'monospace'
                          }}>
                            {formatCardNumber(card.card?.last4)}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: { xs: 4, sm: 4.5, md: 5 } }}>
                          {isPrimary && (
                            <Chip 
                              label="Primary" 
                              size="small" 
                              color="primary"
                              icon={<CheckCircleIcon sx={{ fontSize: { xs: '14px', sm: '16px' } }} />}
                              sx={{ 
                                fontWeight: 600,
                                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                height: { xs: 22, sm: 24 }
                              }}
                            />
                          )}
                        </Box>
                      </Stack>
                    </Box>

                    <Divider sx={{ mx: { xs: 2, sm: 3 } }} />

                    {/* Card Details */}
                    <Box sx={{ p: { xs: 2, sm: 3 }, pt: 2 }}>
                      <Stack spacing={{ xs: 1.5, sm: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ 
                            fontWeight: 600,
                            fontSize: { xs: '0.7rem', sm: '0.75rem' }
                          }}>
                            Cardholder Name
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 500, 
                            mt: 0.5,
                            fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                          }}>
                            {card.billing_details?.name || 'N/A'}
                          </Typography>
                        </Box>
                        
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ 
                            fontWeight: 600,
                            fontSize: { xs: '0.7rem', sm: '0.75rem' }
                          }}>
                            Expires
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500, 
                              mt: 0.5,
                              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                              color: isExpired ? '#d32f2f' : 'text.primary'
                            }}
                          >
                            {card.card?.exp_month ? 
                              `${card.card.exp_month.toString().padStart(2, '0')}/${card.card.exp_year}` : 
                              'N/A'
                            }
                            {isExpired && (
                              <Chip 
                                label="Expired" 
                                size="small" 
                                color="error"
                                sx={{ 
                                  ml: 1, 
                                  fontSize: { xs: '0.65rem', sm: '0.7rem' }, 
                                  height: { xs: 18, sm: 20 }
                                }}
                              />
                            )}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{
                      position: 'absolute',
                      top: { xs: 12, sm: 15 },
                      right: { xs: 12, sm: 16, md: 24 }, // Moved even further right to avoid overlap with Primary pill
                      display: 'flex',
                      flexDirection: 'column',
                      gap: { xs: 0.75, sm: 1 },
                      zIndex: 2,
                      minWidth: { xs: 28, sm: 32 }
                    }}>
                      {/* Set as Primary Button */}
                      {!isPrimary && (
                        <Tooltip title="Set as Primary Card" arrow placement="left">
                          <IconButton
                            size="small"
                            onClick={() => handleSetPrimary(card.id)}
                            sx={{
                              width: { xs: 28, sm: 32 },
                              height: { xs: 28, sm: 32 },
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              '& svg': {
                                fontSize: { xs: '16px', sm: '20px' }
                              }
                              // Removed hover effects - only tooltip shows
                            }}
                          >
                            <StarBorderIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Delete Button */}
                      <Tooltip title="Delete Card" arrow placement="left">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteCard(card.id)}
                          sx={{
                            width: { xs: 28, sm: 32 },
                            height: { xs: 28, sm: 32 },
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            color: '#d32f2f',
                            '& svg': {
                              fontSize: { xs: '16px', sm: '20px' }
                            }
                            // Removed hover effects - only tooltip shows
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Brand Icon */}
                    <Box sx={{
                      position: 'absolute',
                      top: { xs: 12, sm: 14, md: 16 },
                      left: { xs: 12, sm: 14, md: 16 },
                      width: { xs: 36, sm: 38, md: 40 },
                      height: { xs: 36, sm: 38, md: 40 },
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${brandInfo.color} 0%, ${brandInfo.bgColor} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 4px 12px ${brandInfo.color}30`
                    }}>
                      <Typography sx={{ fontSize: { xs: 18, sm: 19, md: 20 }, color: 'white' }}>
                        {brandInfo.icon}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ) : (
        <Card sx={{ 
          mb: { xs: 2.5, sm: 3, md: 4 },
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          border: '2px dashed #e2e8f0'
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              py: { xs: 4, sm: 5, md: 6 },
              gap: { xs: 2, sm: 2.5, md: 3 }
            }}>
              <Box sx={{
                width: { xs: 64, sm: 72, md: 80 },
                height: { xs: 64, sm: 72, md: 80 },
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CreditCardIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: 'text.secondary' }} />
              </Box>
              
              <Box sx={{ textAlign: 'center', px: { xs: 2, sm: 0 } }}>
                <Typography variant="h6" color="text.secondary" sx={{ 
                  fontWeight: 600, 
                  mb: 1,
                  fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                }}>
                  No Saved Cards
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ 
                  maxWidth: 300,
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                }}>
                  Add a payment method below to enable quick and secure transactions for your subscription.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Add New Card Form */}
      <Card sx={{
        background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
        border: '2px solid #e2e8f0',
        borderRadius: 3,
        overflow: 'hidden'
      }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}08 100%)`,
          p: { xs: 2, sm: 2.5, md: 3 },
          borderBottom: `1px solid ${theme.palette.primary.main}20`
        }}>
          <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }}>
            <Box sx={{
              width: { xs: 40, sm: 44, md: 48 },
              height: { xs: 40, sm: 44, md: 48 },
              minWidth: { xs: 40, sm: 44, md: 48 },
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px ${theme.palette.primary.main}30`
            }}>
              <AddIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 22, md: 24 } }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 700, 
                fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                color: theme.palette.primary.main 
              }}>
                Add New Card
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ 
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                display: { xs: 'none', sm: 'block' }
              }}>
                Securely add a new payment method to your account
              </Typography>
            </Box>
          </Stack>
        </Box>

        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 4 } }}>
          <Box component="form" onSubmit={handleAddCard}>
            <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              {/* Cardholder Name */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  fontWeight: 600, 
                  mb: { xs: 1, sm: 1.5 },
                  fontSize: { xs: '0.875rem', sm: '0.9rem' },
                  color: 'text.primary'
                }}>
                  Cardholder Name *
                </Typography>
                <TextField
                  value={cardholderName}
                  onChange={(e) => setCardholderName(e.target.value)}
                  required
                  fullWidth
                  variant="outlined"
                  placeholder="Enter full name as it appears on the card"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      '& input': {
                        padding: { xs: '12px 14px', sm: '14px 16px' }
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2
                      }
                    }
                  }}
                />
              </Box>
              
              {/* Card Number */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  fontWeight: 600, 
                  mb: { xs: 1, sm: 1.5 },
                  fontSize: { xs: '0.875rem', sm: '0.9rem' },
                  color: 'text.primary'
                }}>
                  Card Number *
                </Typography>
                <Box sx={{ 
                  border: '2px solid #e2e8f0', 
                  borderRadius: 2, 
                  p: { xs: 1.5, sm: 2 },
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: theme.palette.primary.main + '40'
                  },
                  '&:focus-within': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 0 0 3px ${theme.palette.primary.main}15`
                  }
                }}>
                  <CardNumberElement 
                    options={{ 
                      style: {
                        ...cardElementStyles,
                        base: {
                          ...cardElementStyles.base,
                          fontSize: '16px',
                          color: '#374151',
                          '::placeholder': { color: '#9ca3af' },
                          border: 'none',
                          padding: '0'
                        }
                      }
                    }} 
                  />
                </Box>
              </Box>
              
              {/* Expiry and CVC */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 600, 
                    mb: { xs: 1, sm: 1.5 },
                    fontSize: { xs: '0.875rem', sm: '0.9rem' },
                    color: 'text.primary'
                  }}>
                    Expiry Date *
                  </Typography>
                  <Box sx={{ 
                    border: '2px solid #e2e8f0', 
                    borderRadius: 2, 
                    p: { xs: 1.5, sm: 2 },
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: theme.palette.primary.main + '40'
                    },
                    '&:focus-within': {
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 3px ${theme.palette.primary.main}15`
                    }
                  }}>
                    <CardExpiryElement 
                      options={{ 
                        style: {
                          ...cardElementStyles,
                          base: {
                            ...cardElementStyles.base,
                            fontSize: '16px',
                            color: '#374151',
                            '::placeholder': { color: '#9ca3af' },
                            border: 'none',
                            padding: '0'
                          }
                        }
                      }} 
                    />
                  </Box>
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 600, 
                    mb: { xs: 1, sm: 1.5 },
                    fontSize: { xs: '0.875rem', sm: '0.9rem' },
                    color: 'text.primary'
                  }}>
                    CVC *
                  </Typography>
                  <Box sx={{ 
                    border: '2px solid #e2e8f0', 
                    borderRadius: 2, 
                    p: { xs: 1.5, sm: 2 },
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: theme.palette.primary.main + '40'
                    },
                    '&:focus-within': {
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 3px ${theme.palette.primary.main}15`
                    }
                  }}>
                    <CardCvcElement 
                      options={{ 
                        style: {
                          ...cardElementStyles,
                          base: {
                            ...cardElementStyles.base,
                            fontSize: '16px',
                            color: '#374151',
                            '::placeholder': { color: '#9ca3af' },
                            border: 'none',
                            padding: '0'
                          }
                        }
                      }} 
                    />
                  </Box>
                </Box>
              </Stack>
              
              {/* Submit Button */}
              <Box sx={{ pt: { xs: 1.5, sm: 2 } }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!stripe || addingCard}
                  startIcon={addingCard ? <CircularProgress size={{ xs: 18, sm: 20 }} color="inherit" /> : undefined}
                  fullWidth
                  size="large"
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    borderRadius: 2,
                    py: { xs: 1.25, sm: 1.5 },
                    fontWeight: 600,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    textTransform: 'none',
                    boxShadow: `0 4px 16px ${theme.palette.primary.main}30`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      boxShadow: `0 6px 20px ${theme.palette.primary.main}40`,
                      transform: 'translateY(-1px)'
                    },
                    '&:disabled': {
                      background: 'rgba(0, 0, 0, 0.12)',
                      color: 'rgba(0, 0, 0, 0.26)',
                      boxShadow: 'none'
                    }
                  }}
                >
                  {addingCard ? 'Adding Card...' : '+ Add Card'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
});

SavedCardsTab.displayName = 'SavedCardsTab';

export default SavedCardsTab;
